import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { legal } from "@/i18n/legal";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { ArrowLeft, Mail, MessageCircle } from "lucide-react";

export default function LegalPage({ kind }) {
  const { lang, t } = useLang();
  const doc = (legal[lang] || legal.en)[kind];
  const common = (legal[lang] || legal.en).common;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${doc.title} · ParaglideTour`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", doc.subtitle);
  }, [doc]);

  return (
    <div data-testid={`legal-page-${kind}`} className="min-h-screen bg-sand text-jungle">
      <Header />

      {/* Header band */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-20 border-b border-jungle/10 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 font-overline text-[10px] text-forest hover:text-terracotta transition-colors mb-6"
          >
            <ArrowLeft size={14} /> {common.back_home}
          </Link>
          <div className="font-overline text-terracotta text-xs mb-3">Legal</div>
          <h1 data-testid="legal-title" className="font-display text-4xl md:text-6xl text-jungle leading-[1.05]">
            {doc.title}
          </h1>
          <p className="mt-4 text-forest text-base md:text-lg max-w-2xl leading-relaxed">
            {doc.subtitle}
          </p>
          <div className="mt-6 font-overline text-[10px] text-forest">
            {common.last_updated}: {doc.updated}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 grid md:grid-cols-[220px_1fr] gap-10 md:gap-16">
          {/* TOC (desktop) */}
          <aside className="hidden md:block md:sticky md:top-32 md:self-start">
            <div className="font-overline text-[10px] text-forest mb-4">On this page</div>
            <ul className="space-y-3">
              {doc.sections.map((s, i) => (
                <li key={i}>
                  <a
                    href={`#s-${i}`}
                    data-testid={`legal-toc-${i}`}
                    className="text-sm text-forest hover:text-terracotta transition-colors block leading-snug"
                  >
                    {s.h}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <article className="prose-legal max-w-none">
            {doc.sections.map((s, i) => (
              <section
                key={i}
                id={`s-${i}`}
                data-testid={`legal-section-${i}`}
                className="pb-10 mb-10 border-b border-jungle/10 last:border-0 last:mb-0 last:pb-0 scroll-mt-32"
              >
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-display text-terracotta text-2xl leading-none">0{i + 1}</span>
                  <h2 className="font-display text-2xl md:text-3xl text-jungle leading-tight">
                    {s.h}
                  </h2>
                </div>
                <p className="text-forest text-base md:text-[17px] leading-relaxed">{s.p}</p>
              </section>
            ))}

            {/* Contact footer */}
            <div className="mt-16 p-8 border border-jungle/15 bg-white">
              <div className="font-overline text-[10px] text-terracotta mb-2">{common.contact_us}</div>
              <div className="font-display text-2xl text-jungle mb-4">Questions about this policy?</div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:hello@paraglidetour.com"
                  data-testid="legal-contact-email"
                  className="btn-ghost"
                >
                  <Mail size={16} /> hello@paraglidetour.com
                </a>
                <a
                  href="https://wa.me/50683455135"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="legal-contact-whatsapp"
                  className="btn-primary"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
